import { useCallback, useMemo, useState } from 'react';

import { Auth0Error, WebAuth } from 'auth0-js';

import { AccountCreateEventProperties, EVENTS, useIdentify, useTrack } from '@arrived/analytics';
import { CONFIG } from '@arrived/config';
import { trackMarketingConversionEvent } from '@arrived/marketing';
import { MARKETING_CONVERSION_EVENTS } from '@arrived/models';

const webAuth = new WebAuth({
  clientID: CONFIG.auth0.clientId,
  domain: CONFIG.auth0.domain,
  responseMode: 'fragment',
  responseType: 'id_token',
  scope: CONFIG.auth0.scope,
});

export type Auth0SignupAccountCreationEventProperties = Partial<Omit<AccountCreateEventProperties, 'email' | 'page'>>;

export interface UseAuth0SignupOptions {
  onLoginError: () => void;
  redirectUri: string | (() => string);
  accountCreationEventProperties?: Auth0SignupAccountCreationEventProperties;
}

export interface UseAuth0SignupRequest {
  email: string;
  password: string;
}

/**
 * Hook to use on pages where we want to implement a signup & login form.
 *
 * The hook returns an object containing a signupAndLogin function that will take an e-mail and password and attempt to
 * sign the user up and log them in. If the signup fails, the stored error will be present in the return auth0Error
 * object and can be used for displaying error messages to the user.
 *
 * @param accountCreationEventProperties properties to be tied to the account creation event.
 * @param onLoginError function that will be invoked when the auth0 login fails (and signup succeeds).
 * @param redirectUri the URI to redirect the user to upon successful login.
 */
export function useAuth0Signup({ accountCreationEventProperties, onLoginError, redirectUri }: UseAuth0SignupOptions) {
  const track = useTrack();
  const identify = useIdentify();
  const [auth0Error, setAuth0Error] = useState<Auth0Error>();

  const signupAndLogin = useCallback(
    ({ email, password }: UseAuth0SignupRequest) => {
      setAuth0Error(undefined);

      return new Promise<void>((resolve, reject) => {
        webAuth.signup(
          {
            connection: 'Username-Password-Authentication',
            email: email,
            password: password,
          },
          (error) => {
            if (error) {
              setAuth0Error(error);
              reject(error);
            } else {
              track(EVENTS.ACCOUNT_CREATED, {
                email,
                page: window.location.pathname,
                ...accountCreationEventProperties,
              });
              trackMarketingConversionEvent({ email, type: MARKETING_CONVERSION_EVENTS.USER_CREATED });
              identify({
                email: email,
                has_had_user_account_login_created: true,
                user_account_login_created: true,
                user_account_login_created_at: new Date().toISOString(),
              });

              webAuth.login(
                {
                  email,
                  password,
                  realm: CONFIG.auth0.connection,
                  redirectUri: typeof redirectUri === 'string' ? redirectUri : redirectUri(),
                },
                (error) => {
                  if (error) {
                    reject(error);
                  } else {
                    resolve();
                  }
                  onLoginError();
                }
              );
            }
          }
        );
      });
    },
    [accountCreationEventProperties, onLoginError, redirectUri]
  );

  return useMemo(
    () => ({
      auth0Error,
      signupAndLogin,
    }),
    [auth0Error, signupAndLogin]
  );
}
